<!-- A widget to add/remove TVDecoder.allowed_menu_categories. -->
<template>
  <div>
    <!-- Dropdown to de/select MenuCategories -->
    <v-menu
      v-model="showAllowedMenuCategoriesMenu"
      :title="$t('tvdecoderAllowedMenuCategoriesChange')"
      offset-y
      :close-on-content-click="false"
    >
      <span slot="activator">
        <v-hover>
          <v-btn
            slot-scope="{hover}"
            class="ma-0 pr-0"
            round
            :flat="!hover"
            :class="{
              white: hover,
              'warning--text': warning,
              'success--text': !warning,
            }"
            data-cy="allowed-menu-categories-select-activator-btn"
          >
            <v-layout align-baseline>
              <div class="subheading font-weight-bold pt-1 pr-2">
                {{ tvdecoder.allowed_menu_categories.length }} /
                {{ orderedMenuCategories.length }}
              </div>
              <span v-if="label">{{ label }}</span>
            </v-layout>
            <v-icon
              v-if="icon"
              v-bind="iconSizeMap"
            >
              {{ icon }}
            </v-icon>
          </v-btn>
        </v-hover>
      </span>
      <v-list>
        <v-list-tile
          v-if="menuId && !hideCategoriesLink"
          :to="{
            name: 'menucategories',
            params: {menuId: menuId},
          }"
          class="msr--text--hover"
        >
          {{ $t('goToMenuCategories') }}
          <v-progress-circular
            v-if="!!busy"
            :size="20"
            :width="2"
            indeterminate
            class="ml-3 msr--text"
          />
        </v-list-tile>
        <v-divider v-if="menuId && !hideCategoriesLink" />
        <v-list-tile
          v-for="menuCategory in orderedMenuCategories"
          :key="
            'menu-category-' +
              menuCategory.id +
              isMenuCategoryAddedToTVDecoder(menuCategory, tvdecoder) +
              getContentModeForMenuCategory(menuCategory, tvdecoder)
          "
          data-cy="allowed-menu-categories-select-option"
        >
          <v-layout
            align-center
            nowrap
          >
            <!-- Category on/off (also sets content mode default value) -->
            <v-chip
              :title="$t('allowedMenuCategoriesSelectTooltipToggleWhole')"
              label
              class="body-1 ma-0 clickable"
              :class="menuCategoryIdToChipClassNames[menuCategory.id]"
              style="user-select: none"
              @click="toggleMenuCategoryOnTVDecoder(menuCategory, tvdecoder)"
            >
              {{ menuCategory.name }}
            </v-chip>
            <!-- Toggle Content Mode (No Highlights | All | Only Highlights) -->
            <v-chip
              v-if="menuCategory.has_menu_item_highlights"
              :title="$t('allowedMenuCategoriesSelectTooltipToggleHighlights')"
              label
              class="
                ma-0
                clickable
                v-chip--less-padding v-chip--no-left-border-radius
              "
              :class="menuCategoryIdToHighlightChipClassNames[menuCategory.id]"
              @click.stop="
                toggleMenuCategoryContentModeOnTVDecoder(
                  menuCategory,
                  tvdecoder
                )
              "
            >
              <img
                src="/static/icon_krone_01.svg"
                width="20"
                height="20"
                :class="menuCategoryIdToCrownIconClassNames[menuCategory.id]"
              >
            </v-chip>
          </v-layout>
        </v-list-tile>
      </v-list>
    </v-menu>
  </div>
</template>

<script>
import {mapState} from 'vuex';
import orderBy from 'lodash.orderby';

import {
  addMenuCategoryToTVDecoder,
  isMenuCategoryAddedToTVDecoder,
  maybeGetMenuCategoryWithTVDecoderContext,
  removeMenuCategoryFromTVDecoder,
  setMenuCategoryContentModeOnTVDecoder,
} from '@/mixins/allowedMenuCategories.js';

export default {
  props: {
    tvdecoder: {
      type: Object,
      required: true,
    },
    menuId: {
      type: [Number, String],
      required: true,
    },
    label: {
      type: String,
      default: '',
    },
    icon: {
      type: String,
      default: 'arrow_drop_down',
    },
    /** The v-icon 'size', e.g. 'medium' or 'large' */
    iconSize: {
      type: String,
      default: 'large',
    },
    warning: {
      type: Boolean,
      default: false,
    },
    /** Can also be a TVDecoder ID aka true */
    busy: {
      type: [Number, Boolean],
      default: false,
    },
    hideCategoriesLink: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showAllowedMenuCategoriesMenu: false,
    };
  },
  computed: {
    iconSizeMap() {
      const map = {};
      map[this.iconSize] = true;
      return map;
    },
    ...mapState({
      menuCategories: (state) => state.common.menuCategories,
    }),
    orderedMenuCategories() {
      return orderBy(this.menuCategories, ['order', 'name']);
    },
    /**
     * Precalculate CSS classes for MenuCategory's main v-chip.
     */
    menuCategoryIdToChipClassNames() {
      const idToClassNames = {};
      const tvdecoder = this.tvdecoder;

      for (const menuCategory of this.menuCategories) {
        let classNamesString = '';

        if (menuCategory.has_menu_item_highlights) {
          classNamesString += 'v-chip--no-right-border-radius ';
        }

        if (
          !this.isMenuCategoryAddedToTVDecoder(menuCategory, tvdecoder) ||
          this.getContentModeForMenuCategory(menuCategory, tvdecoder) ==
            'ONLY_HIGHLIGHTS'
        ) {
          classNamesString += 'grey lighten-3 outlined--dark ';
        }

        if (
          this.isMenuCategoryAddedToTVDecoder(menuCategory, tvdecoder) &&
          this.getContentModeForMenuCategory(menuCategory, tvdecoder) !=
            'ONLY_HIGHLIGHTS'
        ) {
          classNamesString += 'success white--text ';
        }

        idToClassNames[menuCategory.id] = classNamesString;
      }
      return idToClassNames;
    },
    /**
     * Precalculate CSS classes for MenuCategory's highlights v-chip.
     */
    menuCategoryIdToHighlightChipClassNames() {
      const idToClassNames = {};
      const tvdecoder = this.tvdecoder;

      for (const menuCategory of this.menuCategories) {
        let classNamesString = '';

        if (
          !this.isMenuCategoryAddedToTVDecoder(menuCategory, tvdecoder) ||
          this.getContentModeForMenuCategory(menuCategory, tvdecoder) ==
            'NO_HIGHLIGHTS'
        ) {
          classNamesString += 'grey lighten-3 ';
        }

        if (
          !this.isMenuCategoryAddedToTVDecoder(menuCategory, tvdecoder) ||
          this.getContentModeForMenuCategory(menuCategory, tvdecoder) !=
            'NO_HIGHLIGHTS'
        ) {
          classNamesString += 'outlined--dark ';
        }

        if (
          this.getContentModeForMenuCategory(menuCategory, tvdecoder) ==
            'ALL' ||
          this.getContentModeForMenuCategory(menuCategory, tvdecoder) ==
            'ONLY_HIGHLIGHTS'
        ) {
          classNamesString += 'success white--text outlined--white ';
        }

        idToClassNames[menuCategory.id] = classNamesString;
      }
      return idToClassNames;
    },
    /**
     * Precalculate CSS classes for the highlight icon image.
     */
    menuCategoryIdToCrownIconClassNames() {
      const idToClassNames = {};
      const tvdecoder = this.tvdecoder;

      for (const menuCategory of this.menuCategories) {
        let classNamesString = '';

        if (
          this.getContentModeForMenuCategory(menuCategory, tvdecoder) ==
            'ALL' ||
          this.getContentModeForMenuCategory(menuCategory, tvdecoder) ==
            'ONLY_HIGHLIGHTS'
        ) {
          classNamesString += 'filter-to-white-color ';
        }

        idToClassNames[menuCategory.id] = classNamesString;
      }
      return idToClassNames;
    },
  },
  watch: {
    showAllowedMenuCategoriesMenu: function (show) {
      this.$emit('dropdown-changed', show);
    },
  },
  methods: {
    /**
     * To be used from outside of this component via $refs.
     */
    openMenu() {
      this.showAllowedMenuCategoriesMenu = true;
    },
    isMenuCategoryAddedToTVDecoder: isMenuCategoryAddedToTVDecoder,
    maybeGetMenuCategoryWithTVDecoderContext:
      maybeGetMenuCategoryWithTVDecoderContext,
    getContentModeForMenuCategory(menuCategory, tvdecoder) {
      return maybeGetMenuCategoryWithTVDecoderContext(menuCategory, tvdecoder)
        .content_mode;
    },
    addMenuCategoryToTVDecoder(menuCategory, tvdecoder) {
      const vm = this;
      return addMenuCategoryToTVDecoder(menuCategory, tvdecoder, () =>
        vm.$emit('allowed-menu-categories-changed')
      );
    },
    removeMenuCategoryFromTVDecoder(menuCategory, tvdecoder) {
      const vm = this;
      return removeMenuCategoryFromTVDecoder(menuCategory, tvdecoder, () =>
        vm.$emit('allowed-menu-categories-changed')
      );
    },
    setMenuCategoryContentModeOnTVDecoder(
      menuCategory,
      tvdecoder,
      contentMode
    ) {
      const vm = this;
      setMenuCategoryContentModeOnTVDecoder(
        menuCategory,
        tvdecoder,
        contentMode,
        () => vm.$emit('allowed-menu-categories-changed')
      );
    },
    toggleMenuCategoryOnTVDecoder(menuCategory, tvdecoder) {
      const menuCategoryWithTVDecoderContext =
        this.maybeGetMenuCategoryWithTVDecoderContext(menuCategory, tvdecoder);

      if (
        this.isMenuCategoryAddedToTVDecoder(menuCategory, tvdecoder) &&
        menuCategoryWithTVDecoderContext.content_mode == 'ONLY_HIGHLIGHTS'
      ) {
        this.setMenuCategoryContentModeOnTVDecoder(
          menuCategory,
          tvdecoder,
          'ALL'
        );
      } else if (this.isMenuCategoryAddedToTVDecoder(menuCategory, tvdecoder)) {
        this.removeMenuCategoryFromTVDecoder(menuCategory, tvdecoder);
      } else {
        this.addMenuCategoryToTVDecoder(menuCategory, tvdecoder);
      }
    },
    toggleMenuCategoryContentModeOnTVDecoder(menuCategory, tvdecoder) {
      const menuCategoryWithTVDecoderContext =
        this.maybeGetMenuCategoryWithTVDecoderContext(menuCategory, tvdecoder);

      if (menuCategoryWithTVDecoderContext.content_mode == undefined) {
        this.setMenuCategoryContentModeOnTVDecoder(
          menuCategory,
          tvdecoder,
          'ONLY_HIGHLIGHTS'
        );
      } else if (menuCategoryWithTVDecoderContext.content_mode == 'ALL') {
        this.setMenuCategoryContentModeOnTVDecoder(
          menuCategory,
          tvdecoder,
          'NO_HIGHLIGHTS'
        );
      } else if (
        menuCategoryWithTVDecoderContext.content_mode == 'ONLY_HIGHLIGHTS'
      ) {
        this.removeMenuCategoryFromTVDecoder(menuCategory, tvdecoder);
      } else {
        this.setMenuCategoryContentModeOnTVDecoder(
          menuCategory,
          tvdecoder,
          'ALL'
        );
      }
    },
  },
};
</script>

<style scoped>
.v-chip--less-padding.v-chip >>> .v-chip__content {
  padding-left: 6px;
  padding-right: 6px;
}
.v-chip--no-right-border-radius.v-chip >>> .v-chip__content {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0px;
}
.v-chip--no-left-border-radius.v-chip >>> .v-chip__content {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0px;
}
</style>



// WEBPACK FOOTER //
// src/components/AllowedMenuCategoriesSelect.vue